/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-unsafe-optional-chaining */
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { getOneCase } from '@brands/services/cases/getCaseById';
import { ICase } from '@brands/services/cases/types/ICase';
import { addOneReportInternalNote } from '@brands/services/oneReport/addOneReportInternalNote';
import { addOneReportNote } from '@brands/services/oneReport/addOneReportNote';
import { selectAuth } from '@brands/store/selectors/auth';
import { formatDate } from '@brands/Utils/formatDate';
import { checkCollapsibleHeaderColor } from '@brands/Utils/getCollapsibleHeader';
import { getUserRoleName } from '@brands/Utils/getFriendlyName';
import AddIcon from '@mui/icons-material/Add';
import { Divider } from '@mui/material';
import Button from '@mui/material/Button';
import linkifyHtml from 'linkify-html';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';

import { OralHealthReportSubmit } from '../../assets/types';
import styles from '../../styles.module.scss';
import Description from '../PossibleTreatmentNeedsSection/icons/description.svg';
import AddNotesModal from './modal/AddNotesModal';

type NotesSectionType = 'CASE_NOTE' | 'INTERNAL_CASE_NOTE';

type NotesSectionProps = {
  type: NotesSectionType;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  setConsultationForm: React.Dispatch<React.SetStateAction<OralHealthReportSubmit>>;
  consultationForm: OralHealthReportSubmit;
};

const NotesSection = ({
  type,
  thisCase,
  setCase,
  setConsultationForm,
  consultationForm,
}: NotesSectionProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const routeCaseId = useParams().caseId as string;
  const { userInfo } = useAppSelector(selectAuth);

  const isInternal = type === 'INTERNAL_CASE_NOTE';

  const config = useMemo(
    () => ({
      notes: isInternal ? thisCase.one_report?.internal_notes || [] : thisCase.one_report?.notes || [],
      modalTitle: isInternal ? 'Add internal case notes' : 'Add notes',
      sectionTitle: isInternal ? 'Internal case notes' : 'Case notes',
      buttonText: isInternal ? 'Add internal case notes' : 'Add notes',
      permissionKey: isInternal ? 'InternalNotes' : 'Notes',
      iconClassName: isInternal
        ? styles.possibleTreatmentNeedsSectionDivChildInternalNotes
        : styles.possibleTreatmentNeedsSectionDivChildNotes,
    }),
    [isInternal, thisCase.one_report?.internal_notes, thisCase.one_report?.notes],
  );

  const hasNotes = config.notes.length > 0;

  const canEdit = checkCollapsibleHeaderColor(thisCase || '', userInfo, config.permissionKey) === 'edit';

  const openModal = (): void => {
    setConsultationForm((prev) => ({
      ...prev,
      caseNotes: '',
    }));
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setConsultationForm((prev) => ({
      ...prev,
      caseNotes: '',
    }));
    setIsModalOpen(false);
  };

  const saveCaseNotes = async (noteText: string): Promise<void> => {
    if (isInternal) {
      await addOneReportInternalNote(
        thisCase?.one_report.id || '',
        {
          note: noteText,
        },
        userInfo.role.name,
      );
    } else {
      await addOneReportNote(
        thisCase?.one_report.id || '',
        {
          note: noteText,
        },
        userInfo.role.name,
      );
    }

    const currentCase = await getOneCase(Number(routeCaseId));

    setCase((prev) => ({
      ...prev,
      one_report: currentCase.one_report,
      updated_at: currentCase.updated_at,
    }));

    setConsultationForm((prev) => ({
      ...prev,
      caseNotes: '',
    }));
  };

  const sanitizeDisplayHtml = (html: string): string => {
    const linked = linkifyHtml(html || '', {
      defaultProtocol: 'https',
      attributes: { target: '_blank', rel: 'noopener noreferrer' },
    });

    return sanitizeHtml(linked, {
      allowedTags: ['p', 'strong', 'em', 'u', 'ol', 'ul', 'li', 'br', 'span', 'a'],
      allowedAttributes: {
        a: ['href', 'target', 'rel'],
        span: ['class'],
        li: ['data-list'],
      },
    });
  };

  const sortedNotes = useMemo(() => {
    return [...config.notes].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        return 0;
      }

      return dateA.getTime() - dateB.getTime();
    });
  }, [config.notes]);

  return (
    <div
      className={`${styles.possibleTreatmentNeedsSectionDivChild} ${
        hasNotes ? styles.possibleTreatmentNeedsSectionDivChildExist : ''
      }`}
    >
      <div className={styles.possibleTreatmentNeedsSectionDivChildRow}>
        <span className={config.iconClassName}>
          <img src={Description} alt="" />
        </span>

        {!hasNotes ? (
          <Button variant="text" onClick={openModal} disabled={!canEdit}>
            {config.buttonText}
          </Button>
        ) : (
          <div className={styles.existDataDiv}>
            <span>{config.sectionTitle}</span>
            <Button variant="text" onClick={openModal} disabled={!canEdit}>
              <AddIcon /> Add
            </Button>
          </div>
        )}
      </div>

      {hasNotes && (
        <div className={styles.noteBody}>
          {sortedNotes.map((note, index) => (
            <React.Fragment key={note.id || index}>
              <Divider orientation="horizontal" flexItem className={styles.divider} />
              <div className={`fs-exclude ${styles.caseNotesContent}`}>
                <div className={`fs-exclude ${styles.caseNotesContentHeader}`}>
                  <span>{note.last_edited_at ? formatDate(note.last_edited_at) : formatDate(note.created_at)}</span>
                  <Divider orientation="vertical" flexItem className={styles.divider} />
                  <span>
                    {note.created_by.full_name} ({getUserRoleName(note.created_by.role.name)})
                  </span>
                </div>

                <div className={`fs-exclude ${styles.caseNotesContentContent}`}>
                  <span
                    className={styles.caseNotesContentText}
                    dangerouslySetInnerHTML={{ __html: sanitizeDisplayHtml(note.note) }}
                  />
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <AddNotesModal
        open={isModalOpen}
        onClose={closeModal}
        onSave={saveCaseNotes}
        consultationForm={consultationForm}
        setConsultationForm={setConsultationForm}
        thisCase={thisCase}
        type={type}
      />
    </div>
  );
};

export default React.memo(NotesSection);
